import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ArrowLeftIcon, BanknotesIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import FrotaVehicleTabs from '../../components/frota/FrotaVehicleTabs.tsx';

const categoryChip = (cat: string) => {
  if (cat === 'combustivel') return 'bg-green-100 text-green-800';
  if (cat === 'estacionamento') return 'bg-blue-100 text-blue-800';
  if (cat === 'lavagem') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-700';
};

const FrotaCustos: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const now = new Date();
  const defaultFrom = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString()
    .split('T')[0];
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(lastDay);
  const [category, setCategory] = useState('all');

  const { data, isLoading, error } = useQuery(
    ['fleet-costs', id, from, to, category],
    async () => {
      const params: any = { from, to };
      if (category !== 'all') params.category = category;
      const res = await api.get(`/patrimonio/frota/${id}/custos`, { params });
      return res.data;
    },
    { enabled: !!id }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error || !data) {
    return <div className="text-center text-red-500">Erro ao carregar custos.</div>;
  }

  const { items, summary, vehicle: v } = data;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <p className="text-sm text-gray-500">
            Patrimônio / Frota / {v?.licensePlate} / Custos
          </p>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center mt-1">
            <BanknotesIcon className="h-8 w-8 mr-3 text-primary-600" />
            Custos — {v?.make} {v?.model}
          </h1>
        </div>
        {hasPermission('assets.create') && (
          <button
            onClick={() => navigate(`/patrimonio/frota/${id}/custos/novo`)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-medium flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Novo custo
          </button>
        )}
      </div>

      {id && <FrotaVehicleTabs id={id} />}

      <div className="flex flex-wrap items-end gap-3">
        <button
          onClick={() => navigate(`/patrimonio/frota/${id}`)}
          className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center px-3 py-2 rounded-lg border border-gray-200"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Detalhe do veículo
        </button>
        <label className="text-sm">
          <span className="text-gray-500 block mb-1">De</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="text-gray-500 block mb-1">Até</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2" />
        </label>
        <label className="text-sm">
          <span className="text-gray-500 block mb-1">Categoria</span>
          <select value={category} onChange={e => setCategory(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2">
            <option value="all">Todas</option>
            <option value="combustivel">Combustível</option>
            <option value="manutencao">Manutenção</option>
            <option value="pedagio">Pedágio</option>
            <option value="estacionamento">Estacionamento</option>
            <option value="lavagem">Lavagem</option>
            <option value="outros">Outros</option>
          </select>
        </label>
      </div>

      <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-xl px-4 py-3 text-sm">
        Caminho claro para o financeiro: custos marcados com “via Fluxo de caixa” já geraram saída.
        Clique em <strong>Ver no fluxo</strong> para abrir o lançamento.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total do período</p>
          <p className="text-2xl font-bold text-red-600 mt-1 tabular-nums">
            {formatCurrency(summary?.total || 0)}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Combustível</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
            {formatCurrency(summary?.fuel || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {summary?.fuelPct || 0}% do total · {summary?.fuelCount || 0} abastecimentos
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Outros custos</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
            {formatCurrency(summary?.others || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-1">pedágio, estacionamento, lavagem</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Custo / km</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">
            {formatCurrency(summary?.costPerKm || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            com base em {(summary?.monthKm || 0).toLocaleString('pt-BR')} km no período
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="font-semibold text-gray-900">Lançamentos de custo</h2>
          <Link to="/financeiro/fluxo-de-caixa" className="text-primary-600 text-sm font-medium">
            Abrir Fluxo de caixa filtrado →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Data</th>
                <th className="text-left px-5 py-3 font-medium">Categoria</th>
                <th className="text-left px-5 py-3 font-medium">Descrição</th>
                <th className="text-left px-5 py-3 font-medium">Origem financeira</th>
                <th className="text-right px-5 py-3 font-medium">Valor</th>
                <th className="text-right px-5 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-gray-500">
                    Nenhum custo neste período.
                  </td>
                </tr>
              ) : (
                items.map((c: any) => (
                  <tr key={c.id}>
                    <td className="px-5 py-3 font-mono">
                      {new Date(c.costDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${categoryChip(c.category)}`}>
                        {c.categoryLabel}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div>{c.description}</div>
                      {c.liters != null && (
                        <div className="text-xs text-gray-500">
                          {c.liters} L
                          {c.pricePerLiter != null
                            ? ` · ${formatCurrency(c.pricePerLiter)}/L`
                            : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-500">{c.financialOriginLabel}</td>
                    <td className="px-5 py-3 text-right text-red-600 tabular-nums">
                      {formatCurrency(c.amount)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {c.cashMovementId ? (
                        <Link
                          to="/financeiro/fluxo-de-caixa"
                          className="text-primary-600 font-medium"
                        >
                          Ver no fluxo
                        </Link>
                      ) : (
                        <span className="text-gray-400">Detalhe</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FrotaCustos;
